import React from 'react';
import { Helmet } from 'react-helmet';
import SEOMetadata from '@/components/SEOMetadata';
import HeroSection from '@/components/HeroSection';
import ValueProposition from '@/components/ValueProposition';
import CTASection from '@/components/CTASection';
import FinanzcoachingFAQ from '@/components/finanzcoaching/FinanzcoachingFAQ';
import FinanzcoachingInternalLinks from '@/components/seo/FinanzcoachingInternalLinks';
import FinanzcoachingStructuredData from '@/components/seo/FinanzcoachingStructuredData';
import { webPageStructuredData } from '@/data/structuredData';
import { Check, Heart, Brain, Lightbulb } from 'lucide-react';
const FinanzcoachingPage: React.FC = () => {
  return <>
      <SEOMetadata 
        title="Finanzielle Gelassenheit finden | Finanzcoaching Bayern | Jörg Hörmann" 
        description="Deine Beziehung zu Geld verstehen und verändern. Unabhängiges Finanzcoaching in Bayern und remote – frei von Produktinteressen." 
        metaTitle="Finanzielle Gelassenheit finden | Finanzcoaching Bayern | Jörg Hörmann" 
        metaDescription="Deine Beziehung zu Geld verstehen und verändern. Unabhängiges Finanzcoaching in Bayern und remote – frei von Produktinteressen."
        keywords="Finanzcoaching, Beziehung zu Geld, Geldblockaden, finanzielle Klarheit, Geldverhalten, Finanzpsychologie, Nürnberg, Bayern" 
        canonicalUrl="https://joerg-hoermann.de/finanzcoaching" 
        pageName="Finanzcoaching" 
        imageUrl="/lovable-uploads/finanzcoaching-hero.jpg" 
        preloadImages={["/lovable-uploads/finanzcoaching-hero.jpg", "/lovable-uploads/finanzcoaching-walk-talk.jpg"]} 
      />
      
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(webPageStructuredData({
            name: "Finanzcoaching: Entwickle eine gesunde Beziehung zu Geld",
            description: "Professionelles Finanzcoaching für eine gesunde Beziehung zu Geld. Löse finanzielle Blockaden auf und schaffe Klarheit.",
            url: "https://joerg-hoermann.de/finanzcoaching",
            aboutTopics: ["finanzcoaching", "verhaltungsoekonomie", "selbstfuehrung"]
          }))}
        </script>
      </Helmet>

      <FinanzcoachingStructuredData />

      {/* Hero Section */}
      <HeroSection title="Finanzielle Gelassenheit beginnt nicht bei den Zahlen." subtitle="Unabhängiges Coaching für deine persönliche Geldgeschichte" description="Verstehe deine Muster im Umgang mit Geld und finde zu Entscheidungen, hinter denen du stehen kannst. Unabhängig, vertraulich und frei von Produktinteressen." backgroundClass="bg-joerg-50" imageUrl="/lovable-uploads/finanzcoaching-hero.jpg" primaryCTA={{
      text: "Kostenloses Erstgespräch vereinbaren",
      link: "https://zeeg.me/joerghoermann",
      isExternal: true
    }} hideHeroLogo={true} imageObjectPosition="object-center" />


      {/* AI-Summary / AEO Abstract */}
      <section className="sr-only" aria-label="Zusammenfassung" data-speakable="summary">
        <p>Finanzcoaching bei Jörg Hörmann hilft, eine gesunde Beziehung zu Geld zu entwickeln. Durch verhaltensökonomische Erkenntnisse und achtsame Selbstreflexion werden finanzielle Blockaden gelöst und der Weg zu entspannten Finanzentscheidungen geebnet. Verfügbar in Nürnberg, Bayern und remote.</p>
      </section>

      {/* Problem-Solution Matrix */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-joerg-800 mb-6 text-center">Erkennst du dich wieder?</h2>
            <p className="text-joerg-600 text-lg lg:text-xl leading-relaxed">
              Viele Menschen haben eine komplizierte Beziehung zu Geld. Das muss nicht so bleiben.
            </p>
            {/* Internal Link to Blog for SEO */}
            <p className="text-sm text-joerg-500 mt-4">
              Mehr dazu in unserem <a href="/blog/finanzcoaching-vs-finanzberatung" className="text-primary hover:underline font-medium">Finanzcoaching vs. Finanzberatung Ratgeber</a>
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center p-6 rounded-lg bg-orange-50 border border-orange-100">
              <Heart className="h-12 w-12 text-orange-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-joerg-800 mb-3 text-center">Emotionale Geldthemen</h3>
              <p className="text-joerg-600 mb-4 text-center">Geld löst Stress, Angst oder Scham aus</p>
              <div className="text-sm text-joerg-500 text-center">
                → <span className="font-medium">Emotionale Muster verstehen und auflösen</span>
              </div>
            </div>
            
            <div className="text-center p-6 rounded-lg bg-blue-50 border border-blue-100">
              <Brain className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-joerg-800 mb-3 text-center">Geld-Glaubenssätze</h3>
              <p className="text-joerg-600 mb-4 text-center">"Geld ist schlecht" oder "Ich bin nicht gut im Umgang mit Geld"</p>
              <div className="text-sm text-joerg-500 text-center">
                → <span className="font-medium">Glaubenssätze identifizieren und transformieren</span>
              </div>
            </div>
            
            <div className="text-center p-6 rounded-lg bg-green-50 border border-green-100">
              <Lightbulb className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-joerg-800 mb-3 text-center">Finanzielle Orientierung</h3>
              <p className="text-joerg-600 mb-4 text-center">Unsicherheit bei Geldentscheidungen</p>
              <div className="text-sm text-joerg-500 text-center">
                → <span className="font-medium">Klarheit und Selbstvertrauen entwickeln</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Outdoor Coaching USP */}
      <ValueProposition title="Walk & Talk: Finanzcoaching am Rothsee" description="Bewegung aktiviert neue Denkwege und löst mentale Blockaden. In der inspirierenden Naturkulisse des Rothsees entwickelst du einen frischen Blick auf deine Geldthemen und findest zu neuen Lösungsansätzen." image="/lovable-uploads/finanzcoaching-walk-talk.jpg" backgroundColor="bg-joerg-50" reverse={true}>
        <div className="mt-6 space-y-3">
          <div className="flex items-start gap-3">
            <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
            <span className="text-joerg-600">
              Bewegung löst kreative Denkprozesse aus (Neuroplastizität)
            </span>
          </div>
          <div className="flex items-start gap-3">
            <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
            <span className="text-joerg-600">
              Naturumgebung reduziert Stress und öffnet für neue Perspektiven
            </span>
          </div>
          <div className="flex items-start gap-3">
            <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
            <span className="text-joerg-600">
              Entspannte Atmosphäre für tiefgreifende Gespräche
            </span>
          </div>
        </div>
      </ValueProposition>

      {/* Golden Sentence – emotionaler Break */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <p className="max-w-2xl mx-auto text-center italic text-joerg-700 text-lg lg:text-xl leading-loose">
            „Finanzentscheidungen sind zu einem weit größeren Teil emotional, als wir zugeben wollen. Dort, wo Tabellen enden, beginnt das eigentliche Gespräch."
          </p>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 bg-joerg-50/30">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-joerg-800 mb-8">Das sagen Kunden über mein Finanzcoaching</h2>
          <div className="max-w-2xl mx-auto">
            <blockquote className="text-lg italic text-joerg-600 mb-4">
              "Endlich verstehe ich meine Beziehung zu Geld und kann entspannt finanzielle Entscheidungen treffen."
            </blockquote>
            <cite className="text-sm text-joerg-500">— Sarah M., Führungskraft aus Nürnberg</cite>
          </div>
          <div className="mt-8">
            <a href="/kontakt" className="text-primary hover:underline font-medium">
              Weitere Erfahrungsberichte anfordern
            </a>
          </div>
        </div>
      </section>

      <FinanzcoachingFAQ />

      {/* Internal Links Section for SEO */}
      <FinanzcoachingInternalLinks />

      {/* Rechtlicher Hinweis */}
      <section className="container max-w-4xl mx-auto px-4 py-8">
        <div className="p-5 rounded-lg bg-muted/30 border border-muted flex items-start gap-3">
          <span className="text-muted-foreground mt-0.5 flex-shrink-0">ℹ️</span>
          <p className="text-sm text-muted-foreground leading-relaxed">
            <strong>Rechtlicher Hinweis:</strong> Meine Tätigkeit umfasst ausschließlich Coaching und prozessbegleitende Beratung. 
            Es erfolgt keine Vermittlung von Finanz- oder Versicherungsprodukten, keine Anlageberatung zu konkreten 
            Finanzinstrumenten (z.&nbsp;B. ISIN/WKN), keine Rechts- oder Steuerberatung und keine Ausübung von 
            Heilkunde im Sinne der klinischen Psychotherapie oder Psychologie.{" "}
            <a href="/rechtlicher-hinweis" className="text-primary hover:underline underline-offset-4">
              Mehr erfahren →
            </a>
          </p>
        </div>
      </section>


      {/* Final CTA - Single Button Only */}
      <CTASection 
        title="Ein ruhiges Gespräch über deine nächsten Schritte" 
        description="Lass uns unverbindlich herausfinden, ob und wie Finanzcoaching dich begleiten kann." 
        primaryCTA={{
          text: "Jetzt Erstgespräch vereinbaren",
          link: "https://zeeg.me/joerghoermann",
          isExternal: true
        }} 
        backgroundClass="bg-gradient-to-br from-[#EDF5E9] to-[#E8F1FB] text-joerg-800" 
      />
    </>;
};
export default FinanzcoachingPage;